import React from "react";
import { FAVORITE, LIGHT_GRAY } from "./colors";

/* ------------------ Default Placeholder ------------------ */
export const DefaultPlaceholder = ({ className = "", style = {} }) => (
  <div
    className={`flex items-center justify-center bg-gray-100 ${className}`}
    style={style}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={LIGHT_GRAY}
      strokeWidth="1.5"
      className="w-full h-full"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  </div>
);

/* ------------------ Default Error Fallback ------------------ */
export const DefaultError = ({ className = "", style = {} }) => (
  <div
    className={`flex items-center justify-center bg-gray-100 ${className}`}
    style={style}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={FAVORITE}
      strokeWidth="1.5"
      className="w-full h-full"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  </div>
);

/* ------------------ Image With Placeholder + Error ------------------ */
interface ImageWithFallbackProps {
  src?: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
  PlaceholderComponent?: React.ReactNode;
  ErrorComponent?: React.ReactNode;
}

export const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt = "",
  className = "",
  style = {},
  onError,
  PlaceholderComponent = (
    <DefaultPlaceholder className={className} style={style} />
  ),
  ErrorComponent = <DefaultError className={className} style={style} />,
}) => {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);

  // No src → show placeholder
  if (!src) return <>{PlaceholderComponent}</>;

  // Error → show error fallback
  if (error) return <>{ErrorComponent}</>;

  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {/* Placeholder while loading */}
      {!loaded && (
        <div className="absolute inset-0">{PlaceholderComponent}</div>
      )}

      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => {
          setError(true);
          onError?.();
        }}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500
          ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
};
